/**
 * P7 Spec A11 — customer chooses (a) accept remainder + refund missing OR (b) cancel all full refund.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button.js';
import type {
  A11DecisionChoice,
  OrderLivePendingUnavailable,
  UseOrderLiveStatusResult,
} from '../hooks/useOrderLiveStatus.js';

export interface FulfillmentDecisionPanelProps {
  readonly pending: OrderLivePendingUnavailable;
  readonly live: Pick<UseOrderLiveStatusResult, 'submitDecision' | 'error'>;
  readonly statusLabel?: string;
}

export function FulfillmentDecisionPanel({
  pending,
  live,
  statusLabel,
}: FulfillmentDecisionPanelProps): JSX.Element {
  const { t } = useTranslation('customer');
  const [busy, setBusy] = useState<A11DecisionChoice | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const onChoose = async (choice: A11DecisionChoice): Promise<void> => {
    setBusy(choice);
    setLocalError(null);
    try {
      await live.submitDecision(choice);
    } catch (err) {
      setLocalError(
        err instanceof Error
          ? err.message
          : t('account.orders.a11.submitError', {
              defaultValue: 'Could not save your choice. Retry in a moment.',
            })
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
      data-testid="fulfillment-decision-panel"
      aria-labelledby="fulfillment-decision-title"
    >
      <h3
        id="fulfillment-decision-title"
        className="m-0 text-base font-semibold text-[var(--color-text)]"
      >
        {t('account.orders.a11.title', {
          defaultValue: 'Some items are unavailable',
        })}
      </h3>
      <p className="mt-2 m-0 text-sm text-[var(--color-text-muted)]">
        {t('account.orders.a11.body', {
          defaultValue:
            'Staff marked item(s) as unavailable. Choose to collect the rest with a full refund for missing items, or cancel the whole order for a full refund with no penalty.',
        })}
      </p>
      {statusLabel ? (
        <p className="mt-1 m-0 text-xs text-[var(--color-text-muted)]" data-testid="a11-live-status">
          {statusLabel}
        </p>
      ) : null}
      <p className="mt-1 m-0 text-xs text-[var(--color-text-muted)]">
        {pending.scope === 'ORDER'
          ? t('account.orders.a11.scopeOrder', { defaultValue: 'Entire order marked unavailable' })
          : t('account.orders.a11.scopeItem', {
              defaultValue: '{{count}} item(s) marked unavailable',
              count: pending.lineIds.length,
            })}
      </p>
      {pending.items != null && pending.items.length > 0 ? (
        <ul
          className="mt-2 m-0 list-disc pl-5 text-sm text-[var(--color-text)]"
          data-testid="a11-missing-items"
        >
          {pending.items.map((item) => {
            const label =
              item.name != null && item.name.trim().length > 0
                ? item.name.trim()
                : t('account.orders.a11.unnamedItem', {
                    defaultValue: 'Item #{{lineId}}',
                    lineId: item.lineId,
                  });
            return (
              <li key={item.lineId} data-testid={`a11-missing-item-${item.lineId}`}>
                {item.path != null && item.path.length > 0 ? (
                  <a
                    className="font-medium text-[var(--color-primary)] underline"
                    href={item.path}
                    data-testid={`a11-missing-item-link-${item.lineId}`}
                  >
                    {label}
                  </a>
                ) : (
                  <span>{label}</span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {(localError ?? live.error) != null ? (
        <p className="mt-2 m-0 text-sm text-[var(--color-danger)]" role="alert">
          {localError ?? live.error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          data-testid="a11-accept-remainder"
          disabled={busy != null}
          onClick={() => {
            void onChoose('ACCEPT_REMAINDER');
          }}
        >
          {busy === 'ACCEPT_REMAINDER'
            ? t('account.orders.a11.submitting', { defaultValue: 'Saving…' })
            : t('account.orders.a11.acceptRemainder', {
                defaultValue: 'Collect remainder + refund missing',
              })}
        </Button>
        <Button
          type="button"
          intent="secondary"
          data-testid="a11-cancel-all"
          disabled={busy != null}
          onClick={() => {
            void onChoose('CANCEL_ALL');
          }}
        >
          {busy === 'CANCEL_ALL'
            ? t('account.orders.a11.submitting', { defaultValue: 'Saving…' })
            : t('account.orders.a11.cancelAll', {
                defaultValue: 'Cancel order — full refund',
              })}
        </Button>
      </div>
    </section>
  );
}
